import {
  CenteredSpinner,
  getCookieData,
  removeCookieData,
  THEME,
  useSearchParameters,
  Notify,
} from "@digitallab/grid-common-frontend-components";
import { useHistory, useLocation } from "react-router-dom";
import { Formik, FormikHelpers } from "formik";
import { changeTimeFormat } from "../features/log-book/helpers";
import { StepperForm } from "./StepperForm";
import { useEffect, useState, useMemo } from "react";
import ItemWrapper from "../features/log-book/log-book-item-form-dialog/item-context/ItemWrapper";
import { actionLogValidationSchema } from "./actionLogValidationSchema";
import { runLogValidationSchema } from "./runLogValidationSchema";
import { APP_PATHS, RUN_STATUS_FIELD } from "../constants";
import { DigitalLabLogbookInstrumentModelType } from "../models/DigitalLabLogbookInstrumentModelType";
import { withApollo, WithApolloClient } from "react-apollo";
import { useSelector } from "react-redux";

import { createActionLogBulkVariables, createRunLogBulkMutation, createRunLogBulkVariables } from "./mutationHelpers";
import { VALIDATE_ELN_ID } from "../gql/logBooksapi";

import { loadEquipmentsData } from "./loadEquipmentData";
import { CREATE_BULK_DIGITAL_LAB_LOGBOOK_ACTION_LOG } from "../gql/logBooksapi";
import { RootState } from "../reducers";

export interface ISelectValues {
  key: string;
  value: string;
}
interface IEndorAttachment {
  createdAt: string;
  addedBy: string;
  status: string;
  filename: string;
  fileDescription: string;
  checksum: null;
  targetModule: string;
  fielId: string;
  groupId: string;
  bucketRegion: string;
  groupMetadataVersion: string;
}
export interface IActionLogFormValues {
  actionDate: Date;
  actionTime: string;
  action: string;
  description: string;
  ecrNumber?: string;
  operatorUserName?: string;
  externalDocument?: { link: string; name: string }[];
  attachments?: IEndorAttachment[];
  gxpReady: { newValue: ISelectValues | null; shouldBePublished: boolean | null };
  systemStatus: { newValue: ISelectValues | null; shouldBePublished: boolean | null };
  updatedSoftwareVersion?: { newValue: string | null; shouldBePublished: boolean | null };
  logbookConfigurationBaseline?: { newValue: string | null; shouldBePublished: boolean | null };
  logbookConfigurationDetails?: { newValue: string | null; shouldBePublished: boolean | null };
  equipmentReviewers?: { inventoryId: string; reviewers: {
    email: string;
    userId: string
  }[] }[];
  editReason?: string;
  editComment?: string;
}

export interface IRunLogFormValues {
  runStartDate: Date;
  runEndTime?: string;
  runEndDate: Date;
  runStartTime?: string;
  runStatus: keyof typeof RUN_STATUS_FIELD;
  runIdentification?: string;
  samplesProcessed?: string;
  numberOfRuns?: string;
  description: string;
  defectId?: string;
  eLNid?: {
    name?: string;
    eid?: string;
    type?: string;
    owner?: string;
    state?: string;
    objectType?: string;
    modifiedAt?: string;
  };
  mediaType?: ISelectValues[];
  tipsUsed?: ISelectValues;
  gxpRelevantRun?: string;
  operatorUserName?: string;
  assay: string[];
}

export const KEEP_STATUS_VALUE = {
  key: "KEEP_CURRENT_STATUS",
  value: "Keep current status"
};

export const KEEP_STATUS_OPTION = {
  ...KEEP_STATUS_VALUE,
  label: "Keep current status"
};

const DEFAULT_INITIAL_VALUE = {
  newValue: undefined,
  shouldBePublished: false
};

const actionLogInitialValues = {
  actionDate: new Date(),
  actionTime: changeTimeFormat(new Date()),
  gxpReady: DEFAULT_INITIAL_VALUE,
  updatedSoftwareVersion: DEFAULT_INITIAL_VALUE,
  logbookConfigurationBaseline: DEFAULT_INITIAL_VALUE,
  systemStatus: DEFAULT_INITIAL_VALUE,
  logbookConfigurationDetails: DEFAULT_INITIAL_VALUE,
  description: ""
};

const runLogInitialValues = {
  runStartDate: new Date(),
  runEndDate: new Date(),
  runStatus: "COMPLETED", // Default run status to "Completed" - #4996833
  assay: [], // RunSelectWithOwnProps and CustomAutocompleteWithOwnProp that are used for assay input - needs to get an array to work correctly, for undefined it will crush
  mediaType: [], //Here - if log created with null value, then not possible to open it later for edition.
  description: ""
};

export type InstrumentDetailsForStepperModelType = Pick<
  DigitalLabLogbookInstrumentModelType,
  | "belongingToGroup"
  | "configurationDetails"
  | "entryType"
  | "equipmentId"
  | "equipmentModel"
  | "equipmentNickName"
  | "gxpRelevant"
  | "inventoryId"
  | "linkedInstance"
  | "location"
  | "qualificationStatus"
  | "serialNumber"
  | "siteName"
  | "status"
  | "systemStatus"
  | "softwareVersion"
  | "configurationBaseline"
>;

interface IEquipmentList {
  equipmentIdList: string[];
}

interface IEquipmentListWithDetails {
  equipmentListWithDetails: InstrumentDetailsForStepperModelType[];
}

export interface ILogFormParams extends IEquipmentList {
  action?: string;
  description: string;
  runStatus?: string;
  timestamp?: string;
  elnName?: string;
  elnEid?: string;
  elnType?: string;
  elnOwner?: string;
  elnState?: string;
  elnObjectType?: string;
  elnModifiedAt?: string;
}

export type ILogFormValues = IEquipmentListWithDetails & (IActionLogFormValues | IRunLogFormValues);

export const StepperContainer = withApollo(({ client, landingPageAppSyncClient }: { client: WithApolloClient<any>, landingPageAppSyncClient: WithApolloClient<any> }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const { pathname, search } = useLocation();
  const {
    description,
    action = "",
    runStatus = "",
    elnName = "",
    timestamp = "",
    elnEid = null,
    elnType = null,
    elnOwner = null,
    elnState = null,
    elnObjectType = null,
    elnModifiedAt = null,
  } = useSearchParameters(search) as unknown as ILogFormParams;
  const equipmentIdList = useMemo(
    () => {
      const params = new URLSearchParams(search);
      const equipmentIdParam = params.get("equipmentId");
      if (equipmentIdParam) {
        return [equipmentIdParam];
      }

      let cookieData = getCookieData<string[]>(`equipmentIdList${timestamp}`) || [];
      if (cookieData.length) {
        removeCookieData(`equipmentIdList${timestamp}`);
        sessionStorage.setItem(
          `equipmentIdList${timestamp}`,
          JSON.stringify(cookieData)
        );
      } else {
        cookieData = JSON.parse(
          sessionStorage.getItem(`equipmentIdList${timestamp}`) || "[]"
        );
      }
      return cookieData;
    },
    [timestamp, search]
  );
  const eLNid = {
    name: elnName,
    eid: elnEid,
    type: elnType,
    owner: elnOwner,
    state: elnState,
    objectType: elnObjectType,
    modifiedAt: elnModifiedAt
  };
  const [equipmentListWithDetails, setEquipmentListWithDetails] = useState<
    InstrumentDetailsForStepperModelType[] | undefined
  >();
  const isRunLog = pathname.includes(APP_PATHS.RUN_LOG);
  const history = useHistory();
  const systemStatuses = useSelector((store: RootState) => store.runLogsForm.systemStatuses);
  const gxpReadys = useSelector((state: RootState) => state.runLogsForm.gxpReadys);
  const user = useSelector((state: RootState) => state.user);
  useEffect(() => {
    const getEquipments = async () => {
      if (!equipmentListWithDetails || equipmentListWithDetails?.length === 0) {
        if (equipmentIdList?.length > 0) {
          const foundEquipment = await loadEquipmentsData(equipmentIdList, client, history, pathname);
          setEquipmentListWithDetails(foundEquipment);
        } else setEquipmentListWithDetails([]);
      }
    };
    getEquipments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentIdList]);

  const eLNidValidationCheck = async (eLNid: string) => {
    try {
      const result = await client.query({
        query: VALIDATE_ELN_ID,
        fetchPolicy: "no-cache",
        variables: {
          eLNid
        }
      });
      return result?.data?.getElnIdValidation;
    } catch (err) {
      console.warn(err);
      Notify({
        type: "warning",
        icon: "alarm",
        title: "",
        text: "Unable to verify eLN ID (either eLN or the interface is down)",
        closeButton: true
      });
    } finally {
    }
  };

  const handleSubmit = async (isRunLog: boolean, values: ILogFormValues, actions: FormikHelpers<ILogFormValues>) => {
    if (isRunLog && "eLNid" in values && values.eLNid) {
      const eLNidValidation = await eLNidValidationCheck(values.eLNid?.name || "");
      switch (eLNidValidation?.statusCode) {
        case 400:
          Notify({
            type: "warning",
            icon: "alarm",
            title: "",
            text: "Unable to verify eLN ID (either eLN or the interface is down)",
            closeButton: true
          });
          break;
        case 403:
          Notify({
            type: "warning",
            icon: "alarm",
            title: "",
            text: "Invalid eLN experiment ID",
            closeButton: true
          });
          break;
        case 200:
          break;
        default:
          Notify({
            type: "warning",
            icon: "alarm",
            title: "",
            text: `Unexpected response - status: ${eLNidValidation?.statusCode}`,
            closeButton: true
          });
      }
      if (eLNidValidation?.statusCode !== 200) {
        actions.setSubmitting(false);
        return;
      }
    }
    const returnPath = sessionStorage.getItem("addLogReturnPath");
    sessionStorage.removeItem("addLogReturnPath");
    history.push(returnPath || APP_PATHS.ROOT);
    const { equipmentListWithDetails, ...formValues } = values;

    const logType = isRunLog ? "Run" : "Action";
    try {
      const missingIds = equipmentListWithDetails.filter((item) => !item.inventoryId);
      if (missingIds.length > 0) {
        throw new Error("non-existent Inventory ID");
      }
      const response = await client.mutate({
        mutation: isRunLog
          ? createRunLogBulkMutation(equipmentListWithDetails)
          : CREATE_BULK_DIGITAL_LAB_LOGBOOK_ACTION_LOG,
        fetchPolicy: "no-cache",
        errorPolicy: "all",
        variables: isRunLog ?
          createRunLogBulkVariables(formValues as IRunLogFormValues, equipmentListWithDetails, {
            user,
            systemStatuses,
            gxpReadys
          }) :
          createActionLogBulkVariables(formValues as IActionLogFormValues, equipmentListWithDetails, {
            user,
            systemStatuses,
            gxpReadys
          })
      });
      if (response.data === null || response.data.createBulkDigitalLabLogbookLogSheetChange === null)
        throw new Error("all failed");
      if (response.data || response.errors) {
        if (response.errors) {
          const idsFromErrors = (response.errors as { errorInfo: { inventoryId: string } }[]).map(
            (error) => error.errorInfo?.inventoryId
          );
          Notify({
            title: "",
            icon: "circle_confirm",
            type: "success",
            text: `Successfully added ${equipmentListWithDetails.length - idsFromErrors.length} ${logType} log(s)!`
          });
          Notify({
            title: "",
            type: "warning",
            icon: "alarm",
            text: `Failed to add ${logType} log(s) for ${idsFromErrors.join(", ")}`,
            toastOptions: {
              autoClose: false
            }
          });
        } else
          Notify({
            title: "",
            icon: "circle_confirm",
            type: "success",
            text: `Successfully added ${logType} log(s)!`
          });
      }
    } catch (error) {
      if ((error as Error).message === "non-existent Inventory ID")
        Notify({
          title: "",
          type: "warning",
          icon: "alarm",
          text: `Log edit/creation failed due to non-existent Inventory ID`,
          toastOptions: {
            autoClose: false
          }
        });
      else
        Notify({
          title: "",
          type: "warning",
          icon: "alarm",
          text: `Failed to add ${logType} log(s) for all selected equipment(s)!`
        });
    }
  };

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: THEME["one-color-background-neutral-1"],
        padding: THEME["one-spacer-16"],
        boxSizing: "border-box"
      }}
    >
      {equipmentListWithDetails ? (
        <Formik
          initialValues={
            isRunLog ?
              {
                ...runLogInitialValues,
                description,
                runStatus,
                eLNid,
                equipmentListWithDetails
              } :
              {
                ...actionLogInitialValues,
                description,
                action,
                equipmentListWithDetails
              }
          }
          validationSchema={isRunLog ? runLogValidationSchema : actionLogValidationSchema}
          onSubmit={(values, actions) => {
            handleSubmit(isRunLog, values as ILogFormValues, actions as FormikHelpers<ILogFormValues>);
          }}
        >
          <ItemWrapper setOpenDialog={setOpenDialog} openDialog={openDialog}>
            <StepperForm
              equipmentList={equipmentListWithDetails}
              setEquipmentListWithDetails={setEquipmentListWithDetails}
              client={client}
              landingPageAppSyncClient={landingPageAppSyncClient}
              startAtStep={new URLSearchParams(search).has("equipmentId") ? 1 : 0}
            />
          </ItemWrapper>
        </Formik>
      ) : (
        <CenteredSpinner />
      )}
    </div>
  );
});
